// Per-row attribution provenance badge, shown as a footer in the description
// cell. Rule vs manual is distinguished by color; the full detail (which rule /
// who / when) is on hover via the native title attribute, matching the cell's
// existing title-based hover. Fed the `attribution` object from the row query.

// Permissive nullability to match Relay-generated nested scalar types
// (`string | null | undefined`).
export type ProvenanceAttribution = {
  readonly source: string;
  readonly performedAt?: string | null;
  readonly user?: { readonly email?: string | null } | null;
  readonly rule?: { readonly regex?: string | null } | null;
};

const BADGE_BASE =
  "shrink-0 inline-block rounded px-1.5 py-0.5 text-[10px] font-medium uppercase";

function formatWhen(performedAt: string | null | undefined): string | null {
  if (!performedAt) return null;
  const date = new Date(performedAt);
  return Number.isNaN(date.getTime()) ? null : date.toLocaleDateString();
}

export function ProvenanceBadge({
  attribution,
}: {
  attribution: ProvenanceAttribution | null | undefined;
}) {
  // A flow with no attribution event (e.g. the deferred single-assign path)
  // reads as an explicit neutral state, not a silently missing badge.
  if (!attribution) {
    return (
      <span
        className={`${BADGE_BASE} bg-gray-50 text-gray-400`}
        title="No attribution record for this transaction"
      >
        No provenance
      </span>
    );
  }

  const when = formatWhen(attribution.performedAt);
  const suffix = when ? ` · ${when}` : "";

  if (attribution.source === "RULE") {
    const regex = attribution.rule?.regex;
    const label = regex ? "Rule" : "Rule (unnamed)";
    const title = regex
      ? `Attributed by rule: ${regex}${suffix}`
      : `Attributed by a rule${suffix}`;
    return (
      <span className={`${BADGE_BASE} bg-blue-100 text-blue-700`} title={title}>
        {label}
      </span>
    );
  }

  if (attribution.source === "MANUAL") {
    const who = attribution.user?.email || "unknown";
    return (
      <span
        className={`${BADGE_BASE} bg-green-100 text-green-700`}
        title={`Assigned manually by ${who}${suffix}`}
      >
        Manual
      </span>
    );
  }

  // AUTO_ON_IMPORT / SUGGESTION_ACCEPTED / LEGACY_SEED / etc. — a neutral badge
  // labeled by source so the row is never unexplained.
  const label = attribution.source.replace(/_/g, " ").toLowerCase();
  return (
    <span
      className={`${BADGE_BASE} bg-gray-100 text-gray-600`}
      title={`Source: ${label}${suffix}`}
    >
      {label}
    </span>
  );
}
